import React, { useEffect, useRef, useState } from 'react';
import { QuoteIcon } from 'lucide-react';
import BotanicalDivider from './BotanicalDivider';

interface StoryChapter {
  id: string;
  title: string;
  content: string;
  quote?: string;
  author?: string;
  images: {
    src: string;
    alt: string;
  }[];
}

const storyChapters: StoryChapter[] = [
  {
    id: 'first-meeting',
    title: 'El primer encuentro',
    content: 'Todo comenzó hace cuatro años en el Pasochoa, cuando por casualidad Carlos y Emi se conocieron en plena época de pandemia. Carlos estaba acampando con sus amigos, Emi subió con los suyos a hacer cumbre, y al bajar, se cruzaron. Entre lluvia, frío y mochilas mojadas, el grupo de Carlos les ofreció un té caliente. Y mientras todos se resguardaban en la carpa, él salió a socializar y contó un poco sobre si mismo: fotógrafo, biólogo, trabaja en turismo, explorador… todo un Indiana Jones. Emi escuchaba atenta hasta que él mencionó su año de graduación de universidad. Hizo cálculos rápidos y confirmó para si misma: "es bastante mayor que yo". De regreso, Emi comentó con sus amigas lo interesante que le había parecido conocer a alguien así. Ellas, en cambio, solo dijeron: "Se nota que es mayor y tiene mucha barba". Pero la curiosidad ganó, y Emi lo encontró en Instagram. Después de una que otra reacción y algunos mensajes casuales la conversación parecía fluir. Fue ella quien dio el primer paso, invitándolo a salir. Y, sorpresa: hicieron clic de inmediato.',
    images: [
      {
        src: '/lovable-uploads/paisaje.jpg',
        alt: 'Emi y Carlos en sus primeros momentos juntos'
      },
      {
        src: '/lovable-uploads/montaña.jpg',
        alt: 'Montaña donde Emi y Carlos se conocieron'
      }
    ]
  },
  {
    id: 'journey-together',
    title: 'El camino juntos',
    content: 'Al principio, Carlos parecía querer ser sólo amigos. Emi, por su parte, estaba a punto de empezar su rural y no tenía idea de dónde viviría durante todo ese año. Así que acordaron disfrutar el verano y dejar que las cosas fluyeran. Y fluyeron… tanto que, mientras Emi vivía y trabajaba en Esmeraldas no podía esperar a su visita mensual a Quito para reencontrarse con su novio. Y cuando parecía que la distancia no podía aumentar, Emi se mudó dos años a México para hacer su posgrado en odontología pediátrica. Dos años de vuelos, bienvenidas y despedidas en aeropuertos y muchas videollamadas, lograron lo que parecía imposible: seguir construyendo su relación a pesar de la distancia.',
    images: [
      {
        src: '/lovable-uploads/fotoemi8.jpg',
        alt: 'Emi y Carlos durante su tiempo juntos'
      },
      {
        src: '/lovable-uploads/fotoemi2.JPEG',
        alt: 'Paisaje que representa su viaje juntos'
      }
    ]
  },
  {
    id: 'proposal',
    title: 'La aventura continúa',
    content: 'Entre todo eso, no dejaron de compartir aventuras. Han viajado, explorado montañas, recorrido caminos en bicicleta y, por si fuera poco, se han lanzado dos veces al reto del Huairasinchi, sobreviviendo juntos a la locura de las carreras de aventura. Y ahora, después de tanta distancia, desafíos y kilómetros recorridos, están listos para su mayor aventura: ¡casarse y estar juntos para siempre!',
    images: [
      {
        src: '/lovable-uploads/fotoemi3.JPG',
        alt: 'Emi y Carlos en una de sus aventuras'
      },
      {
        src: '/lovable-uploads/fotoemi4.JPG',
        alt: 'Emi y Carlos celebrando su compromiso'
      }
    ]
  }
];

const OurStory: React.FC = () => {
  const [activeChapter, setActiveChapter] = useState<string | null>(null);
  const chapterRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleElements, setVisibleElements] = useState<{[key: string]: boolean}>({});
  
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const chapterObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveChapter(entry.target.id);
        }
      });
    }, observerOptions);

    const elementObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleElements(prev => ({
            ...prev,
            [entry.target.id]: true
          }));
        }
      });
    }, { threshold: 0.1 });

    chapterRefs.current.forEach((ref) => {
      if (ref) chapterObserver.observe(ref);
    });

    // Observe all elements with animation classes
    document.querySelectorAll('.animate-on-view').forEach((el) => {
      elementObserver.observe(el);
    });

    return () => {
      chapterObserver.disconnect();
      elementObserver.disconnect();
    };
  }, []);

  return (
    <section id="our-story" className="relative pt-16 pb-4">
      <div className="text-center mb-8">
        <h2 className="text-4xl md:text-5xl font-parisienne text-forest mb-6">
          Nuestra Historia
        </h2>
      </div>
      
      {/* Divisor botánico superior */}
      <BotanicalDivider position="top" />
      
      {/* Timeline indicator (desktop only) */}
      <div className="hidden lg:block fixed left-16 top-1/2 transform -translate-y-1/2 z-10">
        <div className="h-64 w-0.5 bg-sage/30 relative">
          {storyChapters.map((chapter, index) => (
            <div 
              key={chapter.id}
              className={`absolute w-3 h-3 rounded-full border border-sage transition-all duration-500 ${
                activeChapter === chapter.id 
                  ? 'bg-sage scale-125' 
                  : 'bg-cream'
              }`}
              style={{ 
                top: `${(index / (storyChapters.length - 1)) * 100}%`,
                transform: 'translateX(-50%)'
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Story chapters */}
      <div>
        {storyChapters.map((chapter, index) => (
          <div
            key={chapter.id}
            id={chapter.id}
            ref={(el) => (chapterRefs.current[index] = el)}
            className={`min-h-screen flex items-center justify-center ${
              index === storyChapters.length - 1 ? 'pt-24 pb-1' : 'py-24'
            } relative overflow-hidden`}
          >
            <div className="max-w-5xl mx-auto px-6 relative z-10">
              <div className="flex flex-col gap-8">
                {/* Contenido del capítulo */}
                <div className="w-full">
                  <div className="text-center space-y-8 glass-card p-8 rounded-lg">
                    {/* Chapter title */}
                    <h2 
                      id={`title-${chapter.id}`}
                      className={`text-4xl md:text-5xl font-parisienne text-forest mb-8 animate-on-view transition-all duration-1000 transform ${
                        visibleElements[`title-${chapter.id}`] 
                          ? 'opacity-100 translate-y-0' 
                          : 'opacity-0 translate-y-10'
                      }`}
                      style={{ transitionDelay: '200ms' }}
                    >
                      {chapter.title}
                    </h2>
                    
                    {/* Chapter content - Tamaño de texto reducido para móvil en el primer capítulo */}
                    <p 
                      id={`content-${chapter.id}`}
                      className={`font-baskerville text-forest/80 leading-relaxed 
                        ${chapter.id === 'first-meeting' ? 'text-base md:text-lg' : 'text-lg'} 
                        animate-on-view transition-all duration-1000 transform ${
                        visibleElements[`content-${chapter.id}`] 
                          ? 'opacity-100 translate-y-0' 
                          : 'opacity-0 translate-y-10'
                      }`}
                      style={{ transitionDelay: '400ms' }}
                    >
                      {chapter.content}
                    </p>
                    
                    {/* Quote - solo mostrar si existe */}
                    {chapter.quote && (
                      <div 
                        id={`quote-${chapter.id}`}
                        className={`relative mt-12 pt-8 animate-on-view transition-all duration-1000 transform ${
                          visibleElements[`quote-${chapter.id}`] 
                            ? 'opacity-100 translate-y-0' 
                            : 'opacity-0 translate-y-10'
                        }`}
                        style={{ transitionDelay: '600ms' }}
                      >
                        <QuoteIcon className="absolute top-0 left-0 text-sage/40 w-8 h-8" />
                        <p className="text-xl italic text-forest/90 mb-2">
                          "{chapter.quote}"
                        </p>
                        {chapter.author && <p className="text-forest/70">— {chapter.author}</p>}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Imágenes del capítulo - dos por capítulo */}
                <div 
                  id={`images-${chapter.id}`}
                  className={`grid md:grid-cols-2 gap-6 animate-on-view transition-all duration-1000 transform ${
                    visibleElements[`images-${chapter.id}`] 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: '600ms' }}
                >
                  {chapter.images.map((image, imgIndex) => (
                    <div 
                      key={`${chapter.id}-img-${imgIndex}`}
                      className="relative"
                    >
                      <div className="overflow-hidden rounded-lg shadow-xl">
                        <img 
                          src={image.src} 
                          alt={image.alt}
                          className="w-full h-[300px] object-cover transform hover:scale-105 transition-transform duration-700"
                          style={{ 
                            objectPosition: 
                              chapter.id === 'journey-together' && imgIndex === 1 
                                ? 'center 65%'
                                : 'center'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Divisor botánico inferior */}
      <BotanicalDivider position="bottom" className="mt-0" />
    </section>
  );
};

export default OurStory;
